import {
  triggerKind,
  type CustomGraph,
  type TestRecordKind,
  type TestRecordOption,
  type TriggerConfig,
} from '@/types/custom-playbooks';

/** The record a test or sample starts from (W2-56). */
export type PickedTestRecord = TestRecordOption;

/** A pick and what it was made for: the play and its saved trigger's
 *  scope (`testPickScope`). */
export interface TestPick {
  scope: string;
  record: PickedTestRecord;
}

/** What a test of this trigger can start from: the event triggers that may
 *  have no event yet. Null when the trigger tests on its own records. */
export function testRecordKind(
  trigger: Partial<TriggerConfig> | undefined
): TestRecordKind | null {
  if (!trigger) return null;
  const kind = triggerKind(trigger);
  if (trigger.source === 'job_change' && kind === 'job_change')
    return 'contact';
  if (trigger.source === 'calendar') return 'contact';
  // A Fireflies or Fathom call: the contact joins a made-up call.
  if (
    (trigger.source === 'fireflies' && kind === 'fireflies_call') ||
    (trigger.source === 'fathom' && kind === 'fathom_call') ||
    (trigger.source === 'gong' && kind === 'gong_call')
  )
    return 'contact';
  if (
    trigger.source === 'crm' &&
    (kind === 'deal_stage' || kind === 'deal_stalled')
  )
    return 'deal';
  if (trigger.source === 'sequence' && kind === 'sequence_event')
    return 'enrollment';
  return null;
}

/** What a picked record belongs to: a change here (the trigger kind, its
 *  CRM, its sequence) makes an earlier pick meaningless. Null when the
 *  trigger takes no pick. */
export function testRecordScope(
  trigger: Partial<TriggerConfig> | undefined
): string | null {
  const kind = testRecordKind(trigger);
  if (!kind || !trigger) return null;
  return JSON.stringify([
    kind,
    triggerKind(trigger) ?? null,
    trigger.crm?.crmType ?? trigger.jobChange?.crmType ?? null,
    trigger.sequence?.sequenceId ?? null,
  ]);
}

/** What a pick on this play's saved trigger is made for, or null when the
 *  trigger takes no pick. */
export function testPickScope(
  playbookId: string,
  trigger: Partial<TriggerConfig> | undefined
): string | null {
  const scope = testRecordScope(trigger);
  return scope === null ? null : `${playbookId}:${scope}`;
}

/** The picked record while it still fits this play's saved trigger. */
export function currentTestPick(
  pick: TestPick | null | undefined,
  playbookId: string,
  trigger: Partial<TriggerConfig> | undefined
): PickedTestRecord | null {
  const scope = testPickScope(playbookId, trigger);
  return scope !== null && pick?.scope === scope ? pick.record : null;
}

export function graphTrigger(
  graph: CustomGraph | undefined
): Partial<TriggerConfig> | undefined {
  return graph?.nodes.find((node) => node.type === 'trigger')?.config as
    | Partial<TriggerConfig>
    | undefined;
}

/** The reason a pick or its search was refused, in the API's words. */
export function testRecordError(error: unknown): string | undefined {
  const detail = (error as { data?: { detail?: unknown } } | undefined)?.data
    ?.detail;
  if (typeof detail === 'string') return detail;
  const message = (detail as { message?: unknown } | undefined)?.message;
  return typeof message === 'string' ? message : undefined;
}
